import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../../auth/AuthContext";
import {
  checkInToProfile,
  fetchBusinessProfiles,
  getUserEmail,
  isProfileActive,
} from "../../../auth/businessProfile";
import type { BusinessProfile } from "../../../auth/businessProfile";

export default function SelectProfile() {
  const { user, signOut } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState<BusinessProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checkingInId, setCheckingInId] = useState<string | null>(null);
  const returnUrl = searchParams.get("returnUrl") || "/pos-checkin";

  const handleSelect = async (profile: BusinessProfile) => {
    if (checkingInId) return;
    if (!isProfileActive(profile)) {
      setError(
        `The subscription for ${profile.name} is inactive. Please renew it from the Biznify web app to continue.`,
      );
      return;
    }
    setError(null);
    setCheckingInId(profile.id);
    await checkInToProfile(profile);
    navigate(returnUrl, { replace: true });
  };

  useEffect(() => {
    const email = getUserEmail(user);

    if (!email) {
      setError("No email found in the authenticated user profile.");
      setLoading(false);
      return;
    }

    fetchBusinessProfiles(email)
      .then((list) => {
        setProfiles(list);
        // Only one active profile: no need to choose
        if (list.length === 1 && isProfileActive(list[0])) {
          void handleSelect(list[0]);
        }
      })
      .catch(() => {
        setError("Failed to load business profiles.");
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <div className="hidden lg:flex w-1/3 items-center justify-center bg-gradient-to-br from-blue-50 to-teal-50">
        <div className="text-center space-y-4">
          <div className="text-3xl font-bold text-gray-900">Biznify POS</div>
          <p className="text-sm text-gray-600 max-w-xs">
            Please choose your business profile to continue.
          </p>
        </div>
      </div>

      <div className="flex-1 p-6 lg:p-10">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Choose a Business Profile
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                {profiles.length > 1
                  ? "We see you have multiple profiles. Please choose one to continue."
                  : "Check in to your profile to continue."}
              </p>
            </div>
            <button
              type="button"
              onClick={signOut}
              className="shrink-0 px-3 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100 cursor-pointer"
            >
              <i className="ri-logout-box-line mr-1"></i>
              Sign Out
            </button>
          </div>

          {loading && (
            <div className="mt-8 text-gray-500">Loading profiles...</div>
          )}
          {error && (
            <div className="mt-6 text-sm text-red-600 bg-red-50 border border-red-200 rounded px-4 py-3">
              {error}
            </div>
          )}

          {!loading && profiles.length === 0 && !error && (
            <div className="mt-8 text-sm text-gray-500">
              No profiles found for this account.
            </div>
          )}

          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            {profiles.map((profile) => {
              const active = isProfileActive(profile);
              const isCheckingIn = checkingInId === profile.id;
              return (
                <button
                  key={profile.id}
                  onClick={() => handleSelect(profile)}
                  disabled={!!checkingInId}
                  className={`bg-white border rounded-xl p-4 text-left transition disabled:cursor-not-allowed ${
                    active
                      ? "border-gray-200 hover:border-teal-400 hover:shadow-sm cursor-pointer"
                      : "border-gray-200 opacity-70 cursor-pointer"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-lg font-semibold text-gray-900">
                        {profile.name}
                      </div>
                      {profile.email && (
                        <div className="text-xs text-gray-500">{profile.email}</div>
                      )}
                    </div>
                    <span className="px-3 py-1 text-xs rounded-full bg-teal-50 text-teal-700 border border-teal-200 whitespace-nowrap">
                      {isCheckingIn ? "Checking in..." : "Check In"}
                    </span>
                  </div>
                  {(profile.plan || profile.status !== undefined) && (
                    <div className="mt-3 text-xs text-gray-600 flex gap-3 flex-wrap">
                      {profile.plan && (
                        <span className="px-2 py-1 rounded bg-blue-50 text-blue-700 border border-blue-200">
                          {profile.plan}
                        </span>
                      )}
                      {profile.status !== undefined && (
                        <span
                          className={`px-2 py-1 rounded border ${
                            active
                              ? "bg-green-50 text-green-700 border-green-200"
                              : "bg-red-50 text-red-700 border-red-200"
                          }`}
                        >
                          {active ? "Active" : "Inactive"}
                        </span>
                      )}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
